import React from 'react';
import styles from '../pages/assets/css/DetailDashboard.module.css';

const ACRONYMS = new Set(['api', 'dicom', 'mwl', 'mpps', 'hl7', 'fhir', 'sha', 'emr', 'ae', 'aet']);

export const toLabel = (value) => String(value || 'unknown')
  .replace(/_/g, ' ')
  .replace(/\b\w+/g, (word) => (
    ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)
  ));

export const plural = (count, singular, pluralForm = `${singular}s`) => (
  `${count} ${count === 1 ? singular : pluralForm}`
);

export const percent = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

export const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export const statusTone = {
  operational: styles.toneGood,
  active: styles.toneGood,
  approved: styles.toneGood,
  completed: styles.toneGood,
  in_progress: styles.toneInfo,
  pending: styles.toneWarn,
  maintenance: styles.toneWarn,
  inactive: styles.toneWarn,
  disabled: styles.toneWarn,
  suspended: styles.toneWarn,
  non_operational: styles.toneBad,
  retired: styles.toneBad,
  failed: styles.toneBad,
  rejected: styles.toneBad,
  cancelled: styles.toneNeutral,
};

export const StatusPill = ({ value, label }) => (
  <span className={`${styles.pill} ${statusTone[value] || styles.toneNeutral}`}>
    {label || toLabel(value)}
  </span>
);

export const KpiCard = ({ label, value, hint }) => (
  <div className={styles.kpiCard}>
    <span className={styles.kpiLabel}>{label}</span>
    <strong className={styles.kpiValue}>{value}</strong>
    {hint ? <span className={styles.kpiHint}>{hint}</span> : null}
  </div>
);

/** Horizontal bars for a {key: count} map, largest first. */
export const BarList = ({ counts, emptyText, formatKey = (key) => key }) => {
  const rows = Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return <p className={styles.muted}>{emptyText}</p>;
  const max = rows[0][1];
  return (
    <ul className={styles.barList}>
      {rows.map(([key, count]) => (
        <li key={key}>
          <span className={styles.barLabel} title={formatKey(key)}>{formatKey(key)}</span>
          <span className={styles.barTrack}>
            <span className={styles.barFill} style={{ width: `${Math.max(4, percent(count, max))}%` }} />
          </span>
          <span className={styles.barValue}>{count}</span>
        </li>
      ))}
    </ul>
  );
};

/** Single stacked bar with a legend for a {status: count} map, in `order`. */
export const StatusBreakdown = ({ counts, order = [], emptyText, label }) => {
  const rank = (key) => (order.includes(key) ? order.indexOf(key) : order.length);
  const rows = Object.entries(counts || {}).sort(([a], [b]) => rank(a) - rank(b));
  const total = rows.reduce((sum, [, count]) => sum + count, 0);
  if (!total) return <p className={styles.muted}>{emptyText}</p>;
  return (
    <>
      <div className={styles.stackBar} role="img" aria-label={label}>
        {rows.map(([key, count]) => (
          <span
            key={key}
            className={`${styles.stackSegment} ${statusTone[key] || styles.toneNeutral}`}
            style={{ width: `${percent(count, total)}%` }}
            title={`${toLabel(key)}: ${count}`}
          />
        ))}
      </div>
      <ul className={styles.legend}>
        {rows.map(([key, count]) => (
          <li key={key}>
            <span className={`${styles.legendDot} ${statusTone[key] || styles.toneNeutral}`} />
            <span>{toLabel(key)}</span>
            <strong>{count}</strong>
            <span className={styles.muted}>{percent(count, total)}%</span>
          </li>
        ))}
      </ul>
    </>
  );
};

export const RawPayload = ({ data }) => (
  <details className={styles.rawPayload}>
    <summary>Raw payload</summary>
    <pre>{JSON.stringify(data, null, 2)}</pre>
  </details>
);
